import { TrendDirection } from "types/enums";
import { FiTrendingUp, FiTrendingDown, FiMinus } from "react-icons/fi";
import { cx } from "./uiHelpers";

export type CompetitionStatusType =
  | "active"
  | "upcoming"
  | "completed"
  | "neutral";

/**
 * The competition color system with Tailwind CSS classes for both light and dark mode
 * @type {CompetitionColorSystem}
 */
export const competitionColorSystem = {
  /** @property {Object} status - Status-related colors */
  status: {
    /** @property {CompetitionStatusType} active - Colors for "active" status */
    active: {
      /** @property {string} bg - Background color class ('bg-green-500 dark:bg-green-600') */
      bg: "bg-green-500 dark:bg-green-600",
      /** @property {string} text - Text color class ('text-green-700 dark:text-green-400') */
      text: "text-green-700 dark:text-green-400",
      /** @property {string} border - Border color class ('border-green-500 dark:border-green-600') */
      border: "border-b border-green-500 dark:border-gray-50",
      /** @property {string} hoverBg - Hover background color ('hover:bg-green-600 dark:hover:bg-green-700') */
      hoverBg: "hover:bg-green-600 dark:hover:bg-green-700",
      /** @property {string} pillBg - Pill background color ('bg-green-100 dark:bg-green-900') */
      pillBg: "bg-green-100 dark:bg-green-900",
      trend: "text-green-500 dark:text-green-400",
    },
    /** @property {CompetitionStatusType} upcoming - Colors for "upcoming" status */
    upcoming: {
      /** @property {string} bg - Background color class ('bg-blue-500 dark:bg-blue-600') */
      bg: "bg-blue-500 dark:bg-blue-600",
      /** @property {string} text - Text color class ('text-blue-700 dark:text-blue-400') */
      text: "text-blue-700 dark:text-blue-400",
      /** @property {string} border - Border color class ('border-blue-500 dark:border-blue-600') */
      border: "border-blue-500 dark:border-blue-600",
      /** @property {string} hoverBg - Hover background color ('hover:bg-blue-600 dark:hover:bg-blue-700') */
      hoverBg: "hover:bg-blue-600 dark:hover:bg-blue-700",
      /** @property {string} pillBg - Pill background color ('bg-blue-100 dark:bg-blue-900') */
      pillBg: "bg-blue-100 dark:bg-blue-900",
    },
    /** @property {CompetitionStatusType} completed - Colors for "completed" status */
    completed: {
      /** @property {string} bg - Background color class ('bg-yellow-500 dark:bg-yellow-600') */
      bg: "bg-yellow-500 dark:bg-yellow-600",
      /** @property {string} text - Text color class ('text-yellow-700 dark:text-yellow-400') */
      text: "text-yellow-700 dark:text-yellow-400",
      /** @property {string} border - Border color class ('border-yellow-500 dark:border-yellow-600') */
      border: "border-yellow-500 dark:border-yellow-600",
      /** @property {string} hoverBg - Hover background color ('hover:bg-yellow-600 dark:hover:bg-yellow-700') */
      hoverBg: "hover:bg-yellow-600 dark:hover:bg-yellow-700",
      /** @property {string} pillBg - Pill background color ('bg-yellow-100 dark:bg-yellow-900') */
      pillBg: "bg-yellow-100 dark:bg-yellow-900",
      trend: "text-red-500 dark:text-red-400",
    },
    /** @property {CompetitionStatusType} neutral - Colors for "neutral" status */
    neutral: {
      /** @property {string} bg - Background color class ('bg-gray-400 dark:bg-gray-600') */
      bg: "bg-gray-400 dark:bg-gray-600",
      /** @property {string} text - Text color class ('text-gray-700 dark:text-gray-400') */
      text: "text-gray-700 dark:text-gray-400",
      /** @property {string} border - Border color class ('border-gray-400 dark:border-gray-600') */
      border: "border-gray-400 dark:border-gray-600",
      /** @property {string} hoverBg - Hover background color ('hover:bg-gray-500 dark:hover:bg-gray-700') */
      hoverBg: "hover:bg-gray-500 dark:hover:bg-gray-700",
      /** @property {string} pillBg - Pill background color ('bg-gray-100 dark:bg-gray-800') */
      pillBg: "bg-gray-100 dark:bg-gray-800",
      trend: "text-gray-400 dark:text-gray-500 ",
    },
  },
  /** @property {Object} ui - UI element colors */
  ui: {
    /** @property {string} card - Card background ('bg-white dark:bg-gray-800') */
    card: "bg-white dark:bg-gray-800 disabled:bg-gray-300 dark:disabled:bg-zinc-900",
    /** @property {string} cardHover - Card hover effect ('hover:bg-gray-50 dark:hover:bg-gray-700') */
    cardHover: "hover:bg-gray-50 dark:hover:bg-gray-700",
    /** @property {Object} border - Border styles */
    border: {
      /** @property {string} primary - Primary border color ('border-gray-200 dark:border-gray-700') */
      primary:
        "border-gray-200 dark:border-gray-700 disabled:border-gray-200 disabled:dark:border-gray-700 focus:ring-yellow-500 focus:border-yellow-500 ",
      /** @property {string} error - Error border color ('border-red-500 dark:border-red-700') */
      error: "border-red-500 dark:border-red-700",
      /** @property {string} faint - Faint border color ('border-gray-100 dark:border-gray-700') */
      faint: "border-gray-100 dark:border-gray-700",
      /** @property {string} focus - Focus border color ('border-yellow-500 dark:border-yellow-500') */
      focus: "border-yellow-500 dark:border-yellow-500",
    },
    /** @property {string} divider - Divider color ('divide-gray-100 dark:divide-gray-800') */
    divider: "divide-gray-100 dark:divide-gray-800",
    /** @property {string} background - Main background ('bg-gray-50 dark:bg-gray-900') */
    background: "bg-gray-50 dark:bg-gray-900",
    /** @property {string} panel - panel background ('bg-neutral-100 dark:bg-neutral-800') */
    panel: "bg-neutral-100 dark:bg-neutral-800",
    /** @property {string} selected - selected background ('bg-blue-50 dark:bg-blue-900/20') */
    selected: "bg-blue-50 dark:bg-blue-900/20",

    /** @property {string} focus - focus background ('focus:outline-none focus:ring-yellow-500 focus:border-yellow-500') */
    focus: "focus:outline-none focus:ring-yellow-500 focus:border-yellow-500",

    /** @property {string} overlay - overlay background ('bg-black bg-opacity-30') */
    overlay: "bg-black/30",

    /** @property {string} loading - loading background ('bg-gray-400 dark:bg-gray-600') */
    loading: "bg-gray-400 dark:bg-gray-600",

    /** @property {Object} filter - Filter element styling */
    filter: {
      /** @property {string} bg - Background for filters ('bg-white dark:bg-gray-800') */
      bg: "bg-white dark:bg-gray-800",
      /** @property {string} border - Border for filters ('border-gray-200 dark:border-gray-700') */
      border: "border-gray-200 dark:border-gray-700",
      /** @property {string} hover - Hover for filters ('hover:bg-gray-100 dark:hover:bg-zinc-700') */
      hover: "hover:bg-gray-100 dark:hover:bg-zinc-700",
      /** @property {string} shadow - Shadow for filters ('shadow-sm') */
      shadow: "shadow-sm",
      /** @property {string} text - Text color for filters ('text-brandDarkGrey dark:text-gray-200') */
      text: "text-brandDarkGrey dark:text-gray-200",
      /** @property {string} selected - Selected filter ('bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-300') */
      selected: "bg-blue-50 dark:text-blue-300",
      /** @property {string} unselected - Unselected filter ('bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300') */
      unselected: "bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    },
    /** @property {Object} button - Button styling */
    button: {
      /** @property {string} primary - Primary button ('bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800') */
      primary:
        "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800",
      /** @property {string} secondary - Secondary button ('bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700') */
      secondary:
        "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700",
      /** @property {string} branded - Branded button ('border-brandGreen bg-brandGreen hover:bg-brandGreen text-white') */
      branded: "border-brandGreen bg-brandGreen hover:bg-brandGreen text-white",
      /** @property {string} selected - Selected button ('border-yellow-500 bg-yellow-500 hover:bg-yellow-600 hover:cursor-pointer text-black') */
      selected:
        "border-yellow-500 bg-yellow-500 hover:bg-yellow-600 hover:cursor-pointer text-black",
      /** @property {string} disabled - Disabled button ('border-gray-300 bg-gray-300 text-gray-500 cursor-not-allowed') */
      disabled: "border-gray-300 bg-gray-300 text-gray-500 cursor-not-allowed",
      /** @property {string} wizzard - wizzard button ('bg-yellow-100 border-yellow-400 text-yellow-700 dark:bg-yellow-900 dark:border-yellow-700 dark:text-yellow-300') */
      wizard:
        "bg-yellow-100 border-yellow-400 text-yellow-700 dark:bg-yellow-900 dark:border-yellow-700 dark:text-yellow-300",
      /** @property {string} close - Close button ('bg-gray-200 dark:bg-zinc-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-zinc-600') */
      close:
        "bg-gray-200 dark:bg-zinc-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-zinc-600",
    },
    /** @property {Object} icon - Icon color system */
    icon: {
      /** @property {string} primary - Primary icon color ('text-yellow-600 dark:text-yellow-200') */
      primary: "text-yellow-600 dark:text-yellow-200",
      /** @property {string} secondary - Secondary icon color ('text-gray-700 dark:text-gray-300') */
      secondary: "text-gray-700 dark:text-gray-300",
      /** @property {string} muted - Muted icon color ('text-gray-400 dark:text-gray-500') */
      muted: "text-gray-400 dark:text-gray-500",
      /** @property {string} active - Active icon color ('text-green-600 dark:text-green-400') */
      active: "text-green-600 dark:text-green-400",
    },
    /** @property {Object} text - Text color system */
    text: {
      /** @property {string} header - Header text color ('text-gray-900 dark:text-gray-200') */
      header: "text-gray-900 dark:text-gray-200 poppins",
      /** @property {string} primary - Primary text color ('text-gray-900 dark:text-gray-100') */
      primary: "text-gray-700 dark:text-gray-300",
      /** @property {string} secondary - Secondary text color ('text-gray-600 dark:text-gray-300') */
      secondary: "text-zinc-800 dark:text-gray-50",
      subheader: "text-gray-400 dark:text-gray-400",
      /** @property {string} muted - Muted text color ('text-gray-400 dark:text-gray-500') */
      muted: "text-gray-400 dark:text-gray-500",
      /** @property {string} active - Active text color ('text-green-600 dark:text-green-400') */
      active: "text-green-600 dark:text-green-400",
      /** @property {string} completed - Completed text color ('text-yellow-600 dark:text-yellow-400') */
      completed: "text-yellow-600 dark:text-yellow-400",
      /** @property {string} upcoming - Upcoming text color ('text-blue-600 dark:text-blue-400') */
      upcoming: "text-blue-600 dark:text-blue-400",
      /** @property {string} neutral - Neutral text color ('text-gray-500 dark:text-gray-400') */
      neutral: "text-gray-500 dark:text-gray-400",
      /** @property {string} error - Error text color ('text-red-600 dark:text-red-400') */
      error: "text-red-600 dark:text-red-400",
    },
  },
  /** @property {Object} leaderboard - Leaderboard-related colors */
  leaderboard: {
    /** @property {string} header - Header background ('bg-gray-100 dark:bg-gray-700') */
    header: "bg-gray-100 dark:bg-gray-700",
    /** @property {string} row - Row styling alternating ('even:bg-gray-50 dark:even:bg-gray-800 odd:bg-white dark:odd:bg-gray-900 even:hover:bg-gray-100 dark:even:hover:bg-gray-700 odd:hover:bg-gray-50 dark:odd:hover:bg-gray-800') */
    row: "even:bg-gray-50 dark:even:bg-gray-800 odd:bg-white dark:odd:bg-gray-900 even:hover:bg-gray-100 dark:even:hover:bg-gray-700 odd:hover:bg-gray-50 dark:odd:hover:bg-gray-800",
    /** @property {string} highlight - Highlight for user's facility ('bg-yellow-50 dark:bg-yellow-900/30 hover:bg-yellow-300 dark:hover:bg-yellow-500/40') */
    highlight:
      "odd:bg-yellow-200 dark:odd:bg-yellow-400/30 even:bg-yellow-100 dark:even:bg-yellow-300/30 hover:odd:bg-yellow-300 dark:hover:odd:bg-yellow-500/40  hover:even:bg-yellow-200 dark:hover:even:bg-yellow-400/30",
    /** @property {string} position - Position indicator ('text-gray-500 dark:text-gray-400 font-semibold') */
    position: "text-zinc-800 dark:text-gray-400 font-semibold",
    /** @property {string} text - text color indicator ('text-gray-900 dark:text-white') */
    text: "text-gray-900 dark:text-white",
  },
  /** @property {Object} tourVisibility - Tour visibility styles */
  tourVisibility: {
    /** @property {Object} public - Public tour styling */
    public: {
      /** @property {string} bg - Background for public tours ('bg-green-100 dark:bg-green-900/30') */
      bg: "bg-green-100 dark:bg-green-900/30",
      /** @property {string} text - Text color for public tours ('text-green-800 dark:text-green-300') */
      text: "text-green-800 dark:text-green-300",
      /** @property {string} combined - Combined styling for public tours ('bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300') */
      combined:
        "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300",
    },
    /** @property {Object} private - Private tour styling */
    private: {
      /** @property {string} bg - Background for private tours ('bg-purple-100 dark:bg-purple-900/30') */
      bg: "bg-purple-100 dark:bg-purple-900/30",
      /** @property {string} text - Text color for private tours ('text-purple-800 dark:text-purple-300') */
      text: "text-purple-800 dark:text-purple-300",
      /** @property {string} combined - Combined styling for private tours ('bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300') */
      combined:
        "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300",
    },
    unselected: {
      /** @property {string} bg - Background for unknown tours ('bg-gray-100 dark:bg-gray-700') */
      bg: "bg-gray-100 dark:bg-gray-700",
      /** @property {string} text - Text color for unknown  tours ('text-gray-800 dark:text-gray-300') */
      text: "text-gray-800 dark:text-gray-300",
      /** @property {string} combined - Combined styling for unknown tours ('bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300') */
      combined: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300",
    },
  },
  /** @property {Object} eventTypes - Event type styling */
  eventTypes: {
    /** @property {Object} closestToPin - Closest to pin styling */
    closestToPin: {
      /** @property {string} bg - Background for closest to pin ('bg-blue-100 dark:bg-blue-900/30') */
      bg: "bg-blue-100 dark:bg-blue-900/30",
      /** @property {string} text - Text color for closest to pin ('text-blue-700 dark:text-blue-400') */
      text: "text-blue-700 dark:text-blue-400",
      /** @property {string} combined - Combined styling for closest to pin ('bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400') */
      combined:
        "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-700",
    },
    /** @property {Object} longDrive - Long drive styling */
    longDrive: {
      /** @property {string} bg - Background for long drive ('bg-amber-100 dark:bg-amber-900/30') */
      bg: "bg-amber-100 dark:bg-amber-900/30",
      /** @property {string} text - Text color for long drive ('text-amber-800 dark:text-amber-300') */
      text: "text-amber-800 dark:text-amber-300",
      /** @property {string} combined - Combined styling for long drive ('bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300') */
      combined:
        "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-800",
    },
    /** @property {Object} tournament - Tournament styling */
    tournament: {
      /** @property {string} bg - Background for tournament ('bg-indigo-100 dark:bg-indigo-900/30') */
      bg: "bg-indigo-100 dark:bg-indigo-900/30",
      /** @property {string} text - Text color for tournament ('text-indigo-800 dark:text-indigo-300') */
      text: "text-indigo-800 dark:text-indigo-300",
      /** @property {string} combined - Combined styling for tournament ('bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300') */
      combined:
        "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 border-indigo-800",
    },
    unselected: {
      /** @property {string} bg - Background for unknown tours ('bg-gray-100 dark:bg-gray-700') */
      bg: "bg-gray-100 dark:bg-gray-700",
      /** @property {string} text - Text color for unknown  tours ('text-gray-800 dark:text-gray-300') */
      text: "text-gray-800 dark:text-gray-300",
      /** @property {string} combined - Combined styling for unknown tours ('bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300') */
      combined:
        "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-400",
    },
  },

  /** @property {Object} holeHighlight - Styling for hole highlights based on event type */
  holeHighlight: {
    /** @property {Object} par3 - Highlight styling for Par 3 holes (for Closest to Pin) */
    par3: {
      /** @property {string} bg - Background for par 3 holes ('bg-blue-100 dark:bg-blue-800/40') */
      bg: "bg-blue-100 dark:bg-blue-800/40",
      /** @property {string} text - Text color for par 3 holes ('text-blue-700 dark:text-blue-300') */
      text: "text-blue-700 dark:text-blue-300",
      /** @property {string} border - Border for par 3 holes ('border-blue-300 dark:border-blue-700') */
      border: "border-blue-300 dark:border-blue-700",
      /** @property {string} combined - Combined styling for par 3 holes ('bg-blue-100 dark:bg-blue-800/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 border-l-4') */
      combined:
        "bg-blue-100 dark:bg-blue-800/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 border-l-4",
    },
    /** @property {Object} par5 - Highlight styling for Par 5 holes (for Long Drive) */
    par5: {
      /** @property {string} bg - Background for par 5 holes ('bg-amber-100 dark:bg-amber-800/40') */
      bg: "bg-amber-100 dark:bg-amber-800/40",
      /** @property {string} text - Text color for par 5 holes ('text-amber-800 dark:text-amber-300') */
      text: "text-amber-800 dark:text-amber-300",
      /** @property {string} border - Border for par 5 holes ('border-amber-300 dark:border-amber-700') */
      border: "border-amber-300 dark:border-amber-700",
      /** @property {string} combined - Combined styling for par 5 holes ('bg-amber-100 dark:bg-amber-800/40 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-700 border-l-4') */
      combined:
        "bg-amber-100 dark:bg-amber-800/40 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-700 border-l-4",
    },
  },
};

// Get competition status colors
export const getCompetitionStatusStyles = (status: CompetitionStatusType) => {
  const statusColors =
    competitionColorSystem.status[status] ||
    competitionColorSystem.status.neutral;
  return {
    bg: statusColors.bg,
    text: statusColors.text,
    border: statusColors.border,
    hoverBg: statusColors.hoverBg,
    pillBg: statusColors.pillBg,
  };
};

// Get text color based on competition status
export const getCompetitionStatusTextColor = (
  status: CompetitionStatusType,
): string => {
  return (
    competitionColorSystem.ui.text[status] ||
    competitionColorSystem.ui.text.muted
  );
};

// Get badge background color based on competition status
export const getCompetitionStatusBadgeColor = (
  status: CompetitionStatusType,
): string => {
  return (
    competitionColorSystem.status[status].pillBg ||
    competitionColorSystem.status.neutral.pillBg
  );
};

// Get card style with proper hover effect
export const getCompetitionCardStyle = (): string => {
  return `${competitionColorSystem.ui.card} ${competitionColorSystem.ui.border.primary} ${competitionColorSystem.ui.cardHover}`;
};

export const getTrendIcon = (
  direction: TrendDirection,
  className?: string,
): React.ReactElement => {
  let status: CompetitionStatusType;
  let classColor: string;

  switch (direction) {
    case TrendDirection.UP:
      status = "active";
      classColor = competitionColorSystem.status[status].trend;
      return <FiTrendingUp className={cx("w-3 h-3", classColor, className)} />;
    case TrendDirection.DOWN:
      status = "completed";
      classColor = competitionColorSystem.status[status].trend;
      return (
        <FiTrendingDown className={cx("w-3 h-3", classColor, className)} />
      );
    case TrendDirection.FLAT:
    default:
      status = "neutral";
      classColor = competitionColorSystem.status[status].trend;
      return <FiMinus className={cx("w-3 h-3", classColor, className)} />;
  }
};
